import { ExternalLink, RefreshCw } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useHubspotForecast } from "../api";
import { stageVariantFor } from "../constants";
import { fmtCents, fmtCount, fmtPct } from "../format";

/**
 * Live HubSpot Forecasting view — mirrors the columns in HubSpot's
 * /forecasting URL (per-stage Deal count + Amount + Projected (weighted) +
 * stage close-weighting). Behind a "Sync from HubSpot" button on the
 * Summary tab so we don't burn HubSpot rate-limit quota on every dashboard
 * load; React-Query caches the result for 5 minutes once fetched.
 *
 * When the HubSpot Private App token isn't configured (or the backend's
 * env is missing it), the endpoint returns ``{not_configured: true}`` and
 * this card renders a yellow setup-required block instead of erroring.
 */
export function HubspotForecastCard({
  fiscalYear,
  periodType,
  periodValue,
  periodLabel,
  pipeline = "default",
}: {
  fiscalYear: number;
  periodType: "month" | "quarter";
  periodValue: number;
  periodLabel: string;
  pipeline?: string;
}) {
  const [requested, setRequested] = useState(false);
  const query = useHubspotForecast({
    fiscalYear,
    periodType,
    periodValue,
    pipeline,
    enabled: requested,
  });
  const data = query.data;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 pb-2">
        <div className="flex flex-col">
          <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
            HubSpot Forecasting — {periodLabel}
          </CardTitle>
          {data?.fetched_at && !data.not_configured && (
            <span className="mt-0.5 font-mono text-[10px] text-muted-foreground">
              Synced {new Date(data.fetched_at).toLocaleString()}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <a
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-power"
            href={`https://app.hubspot.com/forecasting/21771408/teams/78229669?forecast_type=0&pipeline=default&time_period=${
              periodType === "month" ? "THIS_MONTH" : "THIS_QUARTER"
            }&deal_grouping_type=DEAL_STAGE`}
            target="_blank"
            rel="noreferrer"
          >
            Open in HubSpot <ExternalLink className="h-3 w-3" />
          </a>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setRequested(true);
              void query.refetch();
            }}
            disabled={query.isFetching}
          >
            <RefreshCw
              className={`mr-1 h-3.5 w-3.5 ${query.isFetching ? "animate-spin" : ""}`}
            />
            {data ? "Re-sync" : "Sync from HubSpot"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {!requested && (
          <p className="text-sm text-muted-foreground">
            Click <span className="font-semibold">Sync from HubSpot</span> to
            pull the live deal-stage forecast for this period (uses your
            Private App token).
          </p>
        )}
        {requested && query.isLoading && (
          <p className="text-sm text-muted-foreground">Fetching from HubSpot…</p>
        )}
        {data?.not_configured && (
          <div className="rounded-md border border-warning/40 bg-warning/5 p-3 text-sm">
            <p className="font-semibold text-warning">HubSpot not configured</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Set <code className="rounded bg-muted px-1 font-mono">CINCH_OPS_HUBSPOT_SERVICE_KEY</code>{" "}
              in the backend env to enable live forecast pulls.
            </p>
          </div>
        )}
        {data && !data.not_configured && (
          <ForecastBody data={data} />
        )}
      </CardContent>
    </Card>
  );
}

function ForecastBody({
  data,
}: {
  data: import("../api").HubspotForecastPayload;
}) {
  const totals = data.totals;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Stat label="Deals" value={fmtCount(totals.deal_count)} />
        <Stat label="Amount" value={fmtCents(totals.amount_cents)} />
        <Stat
          label="Projected (weighted)"
          value={fmtCents(totals.projected_amount_cents)}
          tone="positive"
        />
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Stage</TableHead>
            <TableHead className="text-right">Weight</TableHead>
            <TableHead className="text-right">Deals</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead className="text-right">Projected</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.by_stage.map((s) => (
            <TableRow key={s.stage_id}>
              <TableCell>
                <Badge variant={stageVariantFor(s.stage_label)}>{s.stage_label}</Badge>
              </TableCell>
              <TableCell className="text-right font-mono text-xs">
                {fmtPct(s.stage_close_weighting * 100, 0)}
              </TableCell>
              <TableCell className="text-right font-mono">
                {fmtCount(s.deal_count)}
              </TableCell>
              <TableCell className="text-right font-mono">
                {fmtCents(s.amount_cents)}
              </TableCell>
              <TableCell className="text-right font-mono text-success">
                {fmtCents(s.projected_amount_cents)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {Object.keys(data.by_forecast_category).length > 1 && (
        <div className="rounded-md border bg-muted/30 p-3">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
            By forecast category (deal-level ``hs_forecast_category``)
          </p>
          <div className="mt-1.5 flex flex-wrap gap-3 font-mono text-xs">
            {Object.entries(data.by_forecast_category).map(([cat, cents]) => (
              <span key={cat}>
                <span className="text-muted-foreground">{cat}:</span>{" "}
                {fmtCents(cents)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "positive";
}) {
  return (
    <div className="rounded-md border bg-muted/20 p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div
        className={`text-xl font-semibold tabular-nums ${
          tone === "positive" ? "text-success" : ""
        }`}
      >
        {value}
      </div>
    </div>
  );
}
